import React from "react";
import { ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import MarkdownRenderer from "../MarkdownRenderer";
import {
  HELP_INTRO,
  HELP_ARTIFACTS,
  HELP_GETTING_STARTED,
} from "./helpContent";
import HelpCapabilities from "./HelpCapabilities";
import HelpLinks from "./HelpLinks";
import CollapsibleSection from "./CollapsibleSection";

interface HelpPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Collapsible help side panel that slides in from the right, pushing tab
 * content to the left. Organized into collapsible sections for Use Cases,
 * Getting Started, and Need Help.
 *
 * The parent container controls width animation (transition-all duration-300
 * with width toggling between 600px and 0). This component always renders its
 * content; the parent clips overflow when collapsed.
 */
const HelpPanel: React.FC<HelpPanelProps> = ({ onClose }) => {
  return (
    <div
      className="w-[600px] min-w-[600px] border-l border-border bg-background flex flex-col h-full overflow-hidden"
      role="complementary"
      aria-label="Help panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h3 className="text-base font-bold text-foreground">Help</h3>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          aria-label="Close help panel"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Welcome section — always visible */}
        <div className="mb-4 space-y-2">
          <h1 className="text-xl font-semibold mb-6 text-foreground">
            Welcome to TACO
          </h1>
          <div className="mb-6">
            <MarkdownRenderer content={HELP_INTRO} />
          </div>
        </div>

        {/* Collapsible sections */}
        <div className="mt-4">
          <CollapsibleSection title="Capabilities" defaultOpen={true}>
            <HelpCapabilities />
          </CollapsibleSection>

          <CollapsibleSection title="Artifacts" defaultOpen={false}>
            <MarkdownRenderer content={HELP_ARTIFACTS} />
          </CollapsibleSection>

          <CollapsibleSection title="Getting Started" defaultOpen={false}>
            <MarkdownRenderer content={HELP_GETTING_STARTED} />
          </CollapsibleSection>

          <CollapsibleSection title="Need Help?" defaultOpen={false}>
            <HelpLinks />
          </CollapsibleSection>
        </div>
      </div>
    </div>
  );
};

export default HelpPanel;
